import { GPSTrack } from '@nx-smartmonkey/shared/domain';
import { availableMapStyles, AvailableMapStylesType } from '@nx-smartmonkey/ui';

import { Driver } from '../../../../domain/driver/Driver';
import { DriverStatus } from '../../../../domain/driver/DriverStatus';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Stop } from '../../../../domain/stop/Stop';
import { Task } from '../../../../domain/task/Task';
import { PlanDetailsActions, PlanDetailsActionType } from './actions';
import { DriversTableColumn, getDriversTableColumns, setDriversTableColumns } from './tableColumns/driversTableColumns';
import { getStopsTableColumns, setStopsTableColumns, StopsTableColumn } from './tableColumns/stopsTableColumns';
import { getTasksTableColumns, setTasksTableColumns, TasksTableColumn } from './tableColumns/tasksTableColumns';

function getActivePlanMapSymbolFilters() {
  const symbols = localStorage.getItem(`ACTIVE_PLAN_MAP_SYMBOL_FILTERS`);

  const savedSymbols: MapFiltersSymbols = JSON.parse(symbols ?? `{}`);

  mapFilterSymbolsKeys.forEach((symbolKey) => {
    if (symbolKey === `tracks`) savedSymbols[symbolKey] = false;
    else {
      const temp = savedSymbols[symbolKey] ?? defaultMapSymbolFilters[symbolKey];
      savedSymbols[symbolKey] = temp;
    }
  });

  return savedSymbols;
}

function getTypeMapFilters() {
  const filters = localStorage.getItem(`ACTIVE_PLAN_MAP_TYPE`);

  const savedFilters: TypeMapFilters = JSON.parse(filters ?? `{}`);

  return {
    ...defaultTypeMapFilters,
    ...savedFilters,
    mapType: availableMapStyles.includes(savedFilters.mapType) ? savedFilters.mapType : defaultTypeMapFilters.mapType,
  };
}

function setActivePlanMapFilters({ mapType, symbols }: { mapType: MapFilters[`mapType`]; symbols: MapFiltersSymbols }) {
  localStorage.setItem(`ACTIVE_PLAN_MAP_SYMBOL_FILTERS`, JSON.stringify(symbols));
  localStorage.setItem(`ACTIVE_PLAN_MAP_TYPE`, JSON.stringify({ mapType }));
}
type TypeMapFilters = {
  mapType: AvailableMapStylesType;
};
const defaultTypeMapFilters: TypeMapFilters = {
  mapType: `roadmap`,
};

type MapFiltersDrivers = Record<string, boolean>;

const mapFilterSymbolsKeys = [
  `markers`, // Stops
  `lines`, // Routes
  `alerts`, //Alerts
  `zones`, //GeoFences
  `arrows`, //Driver arrows
  `traffic`, //Traffic
  `completed`, //Completed stops
  `tracks`, //Tracks
] as const;

type MapFiltersSymbolsKeys = (typeof mapFilterSymbolsKeys)[number];

type MapFiltersSymbols = Record<MapFiltersSymbolsKeys, boolean>;

const defaultMapSymbolFilters: MapFiltersSymbols = {
  markers: true,
  lines: true,
  alerts: true,
  zones: true,
  arrows: false,
  traffic: false,
  completed: true,
  tracks: false,
};

type OptimizationStatus = `finished` | `failed` | `canceled` | `running`; //Previously there was success instead of finished, but the answer i was getting from the backend was finished

export type OptimizationData = {
  status?: OptimizationStatus;
  startedAt?: Date;
};

export type MapFilters = {
  drivers: MapFiltersDrivers;
  symbols: MapFiltersSymbols;
  mapType?: TypeMapFilters[`mapType`];
};

interface PlanDetailsState {
  plan?: PlanFlat;
  stops: Array<Stop>;
  stopsMap: Record<string, Stop>;
  selectedStops: Array<string>;
  drivers: Array<Driver>;
  driversMap: Record<string, Driver>;
  selectedDrivers: Array<string>;
  tasks: Array<Task>;
  tasksMap: Record<string, Task>;
  selectedTasks: Array<string>;
  geoFences: Array<GeoFence>;
  geoFencesMap: Record<string, GeoFence>;
  mapFilters: MapFilters;
  optimization: OptimizationData;
  editingGeoFence?: GeoFence;
  stopsTableColumns: Array<StopsTableColumn>;
  driversTableColumns: Array<DriversTableColumn>;
  tasksTableColumns: Array<TasksTableColumn>;
}

export const getInitialState = (): PlanDetailsState => {
  return {
    plan: undefined,
    stops: [],
    stopsMap: {},
    selectedStops: [],
    drivers: [],
    driversMap: {},
    selectedDrivers: [],
    tasks: [],
    tasksMap: {},
    selectedTasks: [],
    geoFences: [],
    geoFencesMap: {},
    mapFilters: {
      drivers: {
        [`unassigned`]: true,
      },
      symbols: getActivePlanMapSymbolFilters(),
      mapType: getTypeMapFilters().mapType,
    },
    optimization: {
      status: undefined,
      startedAt: undefined,
    },
    editingGeoFence: undefined,
    stopsTableColumns: getStopsTableColumns(),
    driversTableColumns: getDriversTableColumns(),
    tasksTableColumns: getTasksTableColumns(),
  };
};

const arrayToMap = (elements: Array<Record<string, any>>): Record<string, any> => {
  const map = {} as Record<string, any>;
  elements.forEach((element) => {
    map[element.id] = element;
  });
  return map;
};

export const PlanDetailsReducer = (state: PlanDetailsState, action: PlanDetailsActions): PlanDetailsState => {
  switch (action.type) {
    case PlanDetailsActionType.SET_ACTIVE_PLAN_EDITING_GEO_FENCE:
      return {
        ...state,
        editingGeoFence: action.payload.editingGeoFence,
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA:
      return {
        ...state,
        optimization: {
          status: action.payload.status,
          startedAt: action.payload.startedAt,
        },
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN:
      return {
        ...state,
        plan: action.payload.activePlan,
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN_DRIVERS: {
      const planDrivers = action.payload.planDrivers;
      const driversMap = arrayToMap(planDrivers);
      return {
        ...state,
        drivers: planDrivers,
        driversMap,
      };
    }
    case PlanDetailsActionType.SET_ACTIVE_PLAN_STOPS: {
      const planStops = action.payload.planStops;
      const stopsMap = arrayToMap(planStops);
      return {
        ...state,
        stops: planStops,
        stopsMap,
      };
    }
    case PlanDetailsActionType.SET_ACTIVE_PLAN_GEO_FENCES: {
      const planGeoFences = action.payload.planGeoFences;
      const geoFencesMap = arrayToMap(planGeoFences);
      return {
        ...state,
        geoFences: planGeoFences,
        geoFencesMap,
      };
    }
    case PlanDetailsActionType.UPDATE_PLAN_DRIVER: {
      const planDriver = action.payload.driver as Driver;

      if (!(planDriver.id in state.driversMap)) return state;

      const newDriversMap = { ...state.driversMap };
      newDriversMap[planDriver.id] = planDriver;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        drivers: newDrivers,
        driversMap: newDriversMap,
      };
    }
    case PlanDetailsActionType.UPDATE_PLAN_STOP: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.stopsMap)) return state;

      const newStopsMap = { ...state.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);

      // If updated stop is assigned to a driver, updates its array
      if (planStop.driver_id) {
        const newDriversMap = { ...state.driversMap };

        if (newDriversMap[planStop.driver_id]) {
          newDriversMap[planStop.driver_id].stops = newDriversMap[planStop.driver_id].stops.map((driverStop) => {
            if (driverStop.id === planStop.id) {
              return planStop;
            }
            return driverStop;
          });
        }

        const newDrivers = Object.values(newDriversMap);

        return {
          ...state,
          stops: newStops,
          stopsMap: newStopsMap,
          drivers: newDrivers,
          driversMap: newDriversMap,
        };
      }

      return {
        ...state,
        stops: newStops,
        stopsMap: newStopsMap,
      };
    }
    case PlanDetailsActionType.PLAN_STOP_RESCHEDULED: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.stopsMap)) return state;

      const newStopsMap = { ...state.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);

      // Update the drivers stops
      let updatedDrivers: Driver[] = state.drivers;

      // From assigned to unassigned
      if (!planStop.driver_id) {
        updatedDrivers = state.drivers.map((driver) => {
          driver.stops = driver.stops.map((driverStop) => {
            if (driverStop.id === planStop.id) {
              return planStop;
            }
            return driverStop;
          });
          driver.stops = driver.stops.sort((a, b) => (a.order && b.order ? a.order - b.order : 0));
          return driver;
        });
      } else {
        updatedDrivers = state.drivers.map((driver) => {
          // Updating the actual stop driver
          if (planStop.driver_id === driver.id) {
            // The new stop driver is the same as the last one
            if (driver.stops.find((driverStop) => driverStop.id === planStop.id)) {
              driver.stops = driver.stops.map((driverStop) => {
                if (driverStop.id === planStop.id) {
                  return planStop;
                }
                return driverStop;
              });
            } else {
              // The new stop driver did not have the stop before
              driver.stops = [...driver.stops, planStop];
            }
            driver.stops = driver.stops.sort((a, b) => (a.order && b.order ? a.order - b.order : 0));
            return driver;
          } else {
            // Updating the other drivers that could have that stop
            driver.stops = [...driver.stops.filter((driverStop) => driverStop.id !== planStop.id)];
          }
          driver.stops = driver.stops.sort((a, b) => (a.order && b.order ? a.order - b.order : 0));
          return driver;
        });
      }

      const driversMap = arrayToMap(updatedDrivers);

      return {
        ...state,
        stops: newStops,
        stopsMap: newStopsMap,
        drivers: updatedDrivers,
        driversMap,
      };
    }
    case PlanDetailsActionType.UPDATE_PLAN_DRIVER_LAST_POSITION: {
      const lastPosition = action.payload.lastPosition as GPSTrack | undefined;
      const driverId = action.payload.driverId as string;

      if (!(driverId in state.driversMap)) return state;

      const newDriversMap = { ...state.driversMap };
      newDriversMap[driverId].last_position = lastPosition;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        drivers: newDrivers,
        driversMap: newDriversMap,
      };
    }
    case PlanDetailsActionType.UPDATE_PLAN_DRIVER_STATUS: {
      const status = action.payload.status as DriverStatus;
      const driverId = action.payload.driverId as string;

      if (!(driverId in state.driversMap)) return state;

      const newDriversMap = { ...state.driversMap };
      newDriversMap[driverId].status = status;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        drivers: newDrivers,
        driversMap: newDriversMap,
      };
    }
    case PlanDetailsActionType.CREATE_PLAN_STOP: {
      const planStop = action.payload.stop;
      if (planStop.id in state.stopsMap) return state;

      const newStopsMap = { ...state.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        stops: newStops,
        stopsMap: newStopsMap,
      };
    }
    case PlanDetailsActionType.DELETE_PLAN_STOP: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.stopsMap)) return state;

      const newStopsMap = { ...state.stopsMap };
      delete newStopsMap[planStop.id];
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        stops: newStops,
        stopsMap: newStopsMap,
      };
    }
    case PlanDetailsActionType.CREATE_PLAN_DRIVER: {
      const planDriver = action.payload.driver;
      if (planDriver.id in state.driversMap) return state;

      const newDriversMap = { ...state.driversMap };
      newDriversMap[planDriver.id] = planDriver;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        drivers: newDrivers,
        driversMap: newDriversMap,
      };
    }
    case PlanDetailsActionType.DELETE_PLAN_DRIVER: {
      const planDriver = action.payload.driver;
      if (!(planDriver.id in state.driversMap)) return state;

      const newDriversMap = { ...state.driversMap };
      const stops = newDriversMap[planDriver.id].stops;
      delete newDriversMap[planDriver.id];
      const newStopsMap = { ...state.stopsMap };
      stops.forEach((stop) => {
        newStopsMap[stop.id].unassign();
      });

      const newDrivers = Object.values(newDriversMap);
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        drivers: newDrivers,
        driversMap: newDriversMap,
        stops: newStops,
        stopsMap: newStopsMap,
      };
    }
    case PlanDetailsActionType.SET_ACTIVE_PLAN_MAP_FILTERS: {
      setActivePlanMapFilters({
        symbols: action.payload.mapFilters.symbols,
        mapType: action.payload.mapFilters.mapType,
      });
      return {
        ...state,
        mapFilters: action.payload.mapFilters,
      };
    }
    case PlanDetailsActionType.ON_SELECT_STOP: {
      const { stopId, withControlKey } = action.payload;
      let selectedStops = [stopId];

      if (withControlKey) {
        selectedStops = state.selectedStops.includes(stopId)
          ? state.selectedStops.filter((id) => id !== stopId)
          : [...state.selectedStops, stopId];
      }

      return {
        ...state,
        selectedStops,
      };
    }
    case PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_STOPS:
      return {
        ...state,
        selectedStops: action.payload.selectedStops,
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_DRIVERS:
      return {
        ...state,
        selectedDrivers: action.payload.selectedDrivers,
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_TASKS:
      return {
        ...state,
        selectedTasks: action.payload.selectedTasks,
      };
    case PlanDetailsActionType.SET_STOPS_TABLE_COLUMNS:
      setStopsTableColumns(action.payload);
      return {
        ...state,
        stopsTableColumns: action.payload,
      };
    case PlanDetailsActionType.SET_DRIVERS_TABLE_COLUMNS:
      setDriversTableColumns(action.payload);
      return {
        ...state,
        driversTableColumns: action.payload,
      };
    case PlanDetailsActionType.SET_TASKS_TABLE_COLUMNS:
      setTasksTableColumns(action.payload);
      return {
        ...state,
        tasksTableColumns: action.payload,
      };
    case PlanDetailsActionType.SET_ACTIVE_PLAN_TASKS: {
      const tasks = action.payload.tasks;
      const tasksMap = arrayToMap(tasks);
      return {
        ...state,
        tasks,
        tasksMap,
      };
    }
    case PlanDetailsActionType.ON_SELECT_TASK: {
      const { taskId, withControlKey } = action.payload;
      let selectedTasks = [taskId];

      if (withControlKey) {
        selectedTasks = state.selectedTasks.includes(taskId)
          ? state.selectedTasks.filter((id) => id !== taskId)
          : [...state.selectedTasks, taskId];
      }

      return {
        ...state,
        selectedTasks,
      };
    }
    default:
      return state;
  }
};
